'use client'

import { ENV } from '@/lib/env'
import type { AnalyticsEvent, AnalyticsUser } from './events'
import { event } from '@/lib/analytics/gtag'

class Analytics {
  private user: AnalyticsUser | null = null
  private enabled: boolean

  constructor() {
    this.enabled = ENV.ANALYTICS_ENABLED
  }

  /**
   * Définir l'utilisateur courant
   */
  setUser(user: AnalyticsUser) {
    this.user = user
  }

  /**
   * Tracker un événement
   */
  track<T extends AnalyticsEvent>(eventName: T['name'], properties: T['properties']) {
    if (!this.enabled && ENV.IS_PRODUCTION_MODE) return

    const payload = {
      event: eventName,
      properties: {
        ...properties,
        timestamp: Date.now(),
        url: typeof window !== 'undefined' ? window.location.href : '',
        referrer: typeof document !== 'undefined' ? document.referrer : '',
        user: this.user,
      },
    }

    // Envoyer au backend
    this.sendToBackend(payload)

    // Google Analytics (optionnel)
    event({ action: eventName, ...properties })
  }

  /**
   * Envoyer au backend analytics
   */
  private async sendToBackend(payload: any) {
    try {

      await fetch(ENV.ANALYTICS_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true, // Important pour les événements de sortie de page
      })
    } catch (error) {
      if (ENV.IS_STAGING) {
        console.error(`${new Date().toISOString()} [Next.js] ERROR Analytics error:`, error)
      }
    }
  }

  /**
   * Track page view
   */
  pageView(path: string, locale: string) {
    this.track('page_view', { path, locale })
  }
}

export const analytics = new Analytics()

// Alias pour faciliter l'import
export const trackEvent = analytics.track.bind(analytics)
export const setAnalyticsUser = analytics.setUser.bind(analytics)
export const trackPageView = analytics.pageView.bind(analytics)
